// AGC: Photo Capture Modal - Camera access and photo capture
// AGC: Switch front/back camera, preview before save, GPS capture

import React, { useState, useRef, useEffect } from 'react'
import { Camera, X, RotateCw, Check, Loader2 } from 'lucide-react'
import { Capacitor } from '@capacitor/core'
import { Button } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { useNoteMetadata } from '../../hooks/useNoteMetadata'
import { NoteMetadataStrip } from './NoteMetadataStrip'
import { PermissionBlockedModal } from '../common/PermissionBlockedModal'
import { isMediaPermissionError } from '../../services/permissionHelper'
import type { GPSLocation } from '../../types/lotNotes'
import toast from 'react-hot-toast'

interface PhotoCaptureModalProps {
  isOpen: boolean
  onClose: () => void
  onCapture: (photo: Blob, gpsLocation?: GPSLocation, description?: string, taggedUserIds?: string[]) => void
  lotId: string
}

/**
 * AGC: PhotoCaptureModal - Camera capture with preview
 */
export const PhotoCaptureModal: React.FC<PhotoCaptureModalProps> = ({
  isOpen,
  onClose,
  onCapture,
  lotId,
}) => {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null)
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCameraBlocked, setShowCameraBlocked] = useState(false)

  // AGC: Shared metadata hook — GPS, description, team tags, speech-to-text
  const metadata = useNoteMetadata({ isActive: isOpen })

  // AGC: Start camera when modal opens
  useEffect(() => {
    if (isOpen) {
      startCamera()
    } else {
      stopCamera()
    }

    return () => {
      stopCamera()
    }
  }, [isOpen, facingMode])

  /**
   * AGC: Start camera - uses Capacitor Camera plugin on native, getUserMedia on web
   */
  const startCamera = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // AGC: On native Android, use Capacitor Camera plugin directly (more reliable)
      if (Capacitor.isNativePlatform()) {
        try {
          const { Camera: CapCamera, CameraResultType, CameraSource } = await import('@capacitor/camera')
          const photo = await CapCamera.getPhoto({
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Camera,
            quality: 90,
            width: 1920,
            height: 1080,
            correctOrientation: true,
          })
          if (photo.dataUrl) {
            setCapturedPhoto(photo.dataUrl)
          }
          setIsLoading(false)
          return
        } catch (nativeErr: any) {
          console.warn('AGC: Native camera failed, trying getUserMedia:', nativeErr)
          if (nativeErr?.message?.includes('User cancelled') || nativeErr?.message?.includes('cancelled')) {
            setIsLoading(false)
            handleClose()
            return
          }
          // AGC: Fall through to getUserMedia
        }
      }

      // AGC: Web fallback - getUserMedia
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })

      setStream(mediaStream)

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }

      setIsLoading(false)
    } catch (err) {
      console.error('AGC: Failed to start camera', err)
      if (isMediaPermissionError(err)) {
        setShowCameraBlocked(true)
        setError(null)
      } else {
        setError(t('notes.cameraUnavailable') || 'Câmera não disponível neste dispositivo.')
        toast.error('Câmera não disponível')
      }
      setIsLoading(false)
    }
  }

  /**
   * AGC: Stop camera stream
   */
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }
  }

  /**
   * AGC: Take photo (capture video frame to canvas)
   */
  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0)

    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9)
    setCapturedPhoto(photoDataUrl)

    // AGC: Stop camera after capture
    stopCamera()
  }

  /**
   * AGC: Switch camera (front/back)
   */
  const switchCamera = () => {
    setFacingMode(facingMode === 'user' ? 'environment' : 'user')
  }

  /**
   * AGC: Retake photo - reset state and relaunch camera
   */
  const retake = async () => {
    setCapturedPhoto(null)
    setError(null)
    try {
      await startCamera()
    } catch {
      // AGC: If camera fails on retake, show error
      toast.error(t('notes.cameraUnavailable', 'Câmera não disponível'))
    }
  }

  /**
   * AGC: Save photo
   */
  const save = async () => {
    if (!capturedPhoto) return

    try {
      // AGC: Convert data URL to Blob with correct MIME type
      const response = await fetch(capturedPhoto)
      const rawBlob = await response.blob()
      // AGC: Ensure blob has image/jpeg type for Firebase Storage rules
      const blob = new Blob([rawBlob], { type: 'image/jpeg' })

      onCapture(blob, metadata.gpsLocation, metadata.description, metadata.taggedUserIds)
      handleClose()
    } catch (err) {
      console.error('AGC: Failed to save photo', err)
      toast.error('Erro ao processar foto')
    }
  }

  /**
   * AGC: Close modal and cleanup
   */
  const handleClose = () => {
    stopCamera()
    setCapturedPhoto(null)
    setError(null)
    metadata.reset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90">
      <div className="relative w-full h-full max-w-4xl flex flex-col">
        {/* AGC: Header - safe-area-top so it sits below the Android status bar */}
        <div className="flex items-center justify-between px-4 pb-4 pt-safe-4 bg-black/50">
          <h2 className="text-white text-lg font-semibold">
            {capturedPhoto ? t('notes.photoPreview') : t('notes.takePhoto')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* AGC: Camera/Preview area */}
        <div className="flex-1 relative flex items-center justify-center bg-black">
          {error ? (
            <div className="text-center p-6">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-white mb-2">{error}</p>
              <Button variant="secondary" onClick={startCamera}>
                {t('common.retry')}
              </Button>
            </div>
          ) : isLoading ? (
            <Loader2 className="w-12 h-12 text-white animate-spin" />
          ) : capturedPhoto ? (
            <img
              src={capturedPhoto}
              alt="Captured"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="max-w-full max-h-full object-contain"
            />
          )}

          {/* AGC: Metadata strip — GPS, description, team tags */}
          <NoteMetadataStrip
            gpsLocation={metadata.gpsLocation}
            gpsLoading={metadata.gpsLoading}
            gpsError={metadata.gpsError}
            onRetryGPS={metadata.captureGPS}
            description={metadata.description}
            onDescriptionChange={metadata.setDescription}
            isListening={metadata.isListening}
            isProcessingSpeech={metadata.isProcessingSpeech}
            onToggleSpeechToText={() => metadata.toggleSpeechToText()}
            taggedUserIds={metadata.taggedUserIds}
            onTaggedUsersChange={metadata.setTaggedUserIds}
            variant="overlay"
          />
        </div>

        {/* AGC: Controls - safe-area-bottom so capture buttons clear the Android nav bar */}
        <div className="px-6 pt-6 pb-safe-4 bg-black/50">
          {capturedPhoto ? (
            <div className="flex gap-3 justify-center">
              <Button variant="secondary" onClick={retake} className="flex-1 max-w-xs">
                {t('notes.retake', 'Refazer')}
              </Button>
              <Button onClick={save} icon={<Check className="w-5 h-5" />} className="flex-1 max-w-xs">
                {t('common.save')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-6">
              {/* AGC: Switch camera button */}
              <button
                onClick={switchCamera}
                disabled={isLoading}
                className="p-4 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 transition-colors"
              >
                <RotateCw className="w-6 h-6 text-white" />
              </button>

              {/* AGC: Capture button */}
              <button
                onClick={takePhoto}
                disabled={isLoading || !stream}
                className="w-20 h-20 rounded-full bg-white border-4 border-gray-300 hover:scale-110 disabled:opacity-50 transition-transform"
              >
                <div className="w-full h-full rounded-full bg-white" />
              </button>

              {/* AGC: Placeholder for symmetry */}
              <div className="w-14 h-14" />
            </div>
          )}
        </div>
      </div>

      {/* AGC: Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* AGC: Camera permission blocked modal */}
      <PermissionBlockedModal
        isOpen={showCameraBlocked}
        permissionType="camera"
        onClose={() => {
          setShowCameraBlocked(false)
          handleClose()
        }}
      />
    </div>
  )
}

export default PhotoCaptureModal
